import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/material/styles';
import { INotification } from '../../_api/notification.api';

interface INotificationItemProps {
    notification: INotification;
    onClick?: (notification: INotification) => void;
}

/**
 * NotificationItem Component
 * 
 * Displays a single notification with message, date, time, and read/unread status.
 * Follows Single Responsibility Principle - only renders a notification item.
 */
export const NotificationItem: React.FC<INotificationItemProps> = ({ notification, onClick }) => {
    const theme = useTheme();

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric' 
        });
    };

    const formatTime = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit' 
        });
    };

    const handleClick = () => {
        if (onClick) {
            onClick(notification);
        }
    };

    return (
        <>
            <Box
                sx={{
                    px: 2,
                    py: 1.5,
                    cursor: onClick ? 'pointer' : 'default',
                    backgroundColor: notification.isRead 
                        ? 'transparent' 
                        : theme.palette.action.hover,
                    transition: 'background-color 0.2s',
                    '&:hover': {
                        backgroundColor: notification.isRead
                            ? theme.palette.action.hover
                            : theme.palette.action.selected,
                    },
                }}
                onClick={handleClick}
            >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    {!notification.isRead && (
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: theme.palette.primary.main,
                                mt: 0.75,
                                flexShrink: 0,
                            }}
                        />
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                mb: 0.5,
                                fontWeight: notification.isRead ? 400 : 600,
                                color: notification.isRead 
                                    ? theme.palette.text.secondary 
                                    : theme.palette.text.primary,
                            }}
                        >
                            {notification.message}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary">
                                {formatDate(notification.date)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                •
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {formatTime(notification.date)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
            <Divider />
        </>
    );
};
